import React, { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, ExternalLink } from 'lucide-react';
import {
  useFobPricePulseScorecards,
  useTerminalPricePulseScorecards,
  useVolumeByOriginScorecards,
} from '../../../utils/api-hooks/analytics';
import {
  hasAnyNumber,
  headlineKind,
  FOB_DELTA_KEYS,
  FOB_RANGE_KEYS,
  TERM_HIGHEST_KEYS,
  TERM_DELTA_KEYS,
  TERM_GAP_KEYS,
} from './scorecardVisibility';
import {
  toNumber,
  matchSingleRow,
  pickDefaultOne,
  chooseSharedTuple,
  rangeRows,
  isSentinelValue,
  RELAXED_NOTE,
  DIFFERS_NOTE,
} from './kpiRowSelect';
import { formatDm, formatRangeShort, formatRangeFull, isoWeekLabel, captionText, captionPart, rangeMetaParts } from './kpiFormat';
import CaptionLine from './CaptionLine';

// Metric keys consumed by this file. Centralized so a typo surfaces once,
// not nine times.
const METRICS = {
  FOB_AVG_VS_LY: 'kpi.fob_price_pulse.avg_vs_ly',
  FOB_VS_LAST_WEEK: 'kpi.fob_price_pulse.vs_last_week',
  FOB_EXTREME: 'kpi.fob_price_pulse.extreme_in_range',
  VOL_SUM_VS_LY: 'kpi.volume_by_origin.sum_vs_ly',
  VOL_WOW: 'kpi.volume_by_origin.pct_change_last_week',
  VOL_VS_3YR: 'kpi.volume_by_origin.vs_3yr_avg',
  TERM_HIGHEST: 'kpi.terminal_price_pulse.highest_market',
  TERM_VS_LAST_WEEK: 'kpi.terminal_price_pulse.avg_vs_last_week',
  FOB_TERM_GAP: 'kpi.fob_terminal_gap',
};

// ---------- pure helpers ----------

// Per-metric selection dims, i.e. the dims the metric's rows really carry
// (see the KPI SQL). A dim missing here is never chosen or labelled.
const FOB_PICK = { pctKey: 'delta_pct', valueKey: 'value', supportedKeys: ['origin', 'variety'] };
const TERM_HIGHEST_PICK = { valueKey: 'value', supportedKeys: ['destination', 'variety'] };
// Week-over-week terminal rows carry no selection dims: one aggregate.
const TERM_WOW_PICK = {
  pctKey: 'delta_pct',
  valueKey: 'value',
  supportedKeys: [],
  scopeLabel: 'All terminals',
  scopeTitle: 'Week-over-week change across all terminals and varieties',
};
const TERM_GAP_PICK = { pctKey: 'gap_pct', valueKey: 'terminal_max', supportedKeys: ['variety'] };

const formatUsd = (v) => {
  const n = toNumber(v);
  if (n == null) return '—';
  return `$${n.toFixed(2)}`;
};

const formatSignedUsd = (v) => {
  const n = toNumber(v);
  if (n == null) return '—';
  const abs = Math.abs(n);
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}$${abs.toFixed(2)}`;
};

/**
 * Format a signed percent. Accepts either a fraction (0.372 → "+37.2%") or an
 * already-scaled percent (37.2 → "+37.2%"). The KPI endpoints are inconsistent
 * across metrics — some return fractions, some return percents — so we detect
 * the form by magnitude: |n| ≤ 1 is treated as a fraction, otherwise already
 * in percent units.
 */
const formatSignedPercent = (v) => {
  const n0 = toNumber(v);
  if (n0 == null) return '—';
  const n = Math.abs(n0) <= 1 ? n0 * 100 : n0;
  const abs = Math.abs(n);
  const digits = abs >= 10 ? 0 : 1;
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${sign}${abs.toFixed(digits)}%`;
};

const formatLbsCompact = (v) => {
  const n = toNumber(v);
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M lbs`;
  if (abs >= 10_000) return `${(n / 1_000).toFixed(0)}K lbs`;
  return `${n.toFixed(0)} lbs`;
};

function parseYyyyMmDdLocalDate(s) {
  if (!s || typeof s !== 'string') return null;
  const head = s.trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(head);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const dt = new Date(y, mo - 1, d);
  if (!Number.isFinite(dt.getTime())) return null;
  return dt;
}

function yyyyMmDdFromApiDate(s) {
  if (!s) return '';
  if (typeof s === 'string') {
    const head = s.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatShortDate(s) {
  if (!s) return '';
  const ymd = yyyyMmDdFromApiDate(s);
  const d = parseYyyyMmDdLocalDate(ymd);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Resolve the row for a scorecard tile. Returns null when the backend has
 * signalled `data_available: false` or no rows exist — callers hide the tile.
 */
function getScorecardRow(cards, metricKey, dims, dimAliases) {
  const metric = cards[metricKey];
  if (!metric?.data_available) return null;
  return matchSingleRow(metric.rows, dims, dimAliases);
}

/**
 * Pick the row for a tile: single-select dims resolve exactly as before; when
 * origin / destination / variety is "All" or holds several picks one row is
 * chosen (see pickDefaultOne) and labelled.
 */
function getScorecardPick(cards, metricKey, dims, opts) {
  const metric = cards[metricKey];
  const none = { row: null, label: null, title: null, defaulted: false, tuple: null };
  if (!metric?.data_available) return none;
  return pickDefaultOne(metric.rows, dims, opts);
}

/**
 * Pick every tile of a row once, then again with the tuple chosen by the first
 * tile that ranked on a %, so sibling tiles describe the same selection.
 * Specs are in priority order: { metric, dims, opts }.
 */
function resolvePicks(cards, specs) {
  const run = (extra) => specs.map((s) => getScorecardPick(cards, s.metric, s.dims, { ...s.opts, ...extra }));
  const first = run();
  const tuple = chooseSharedTuple(first);
  return { picks: tuple ? run({ preferTuple: tuple }) : first, tuple };
}

/**
 * Range spread: max(high_value) and min(low_value) over the extreme_in_range
 * rows that match chart dims, restricted to the shared selection when it
 * matches. Labels come from the rows where those extrema occur.
 */
const realValue = (v) => (typeof v === 'string' && v.trim() && !isSentinelValue(v) ? v.trim() : null);

function getFobRangeSpreadAggregated(cards, dims, tuple) {
  const metric = cards[METRICS.FOB_EXTREME];
  if (!metric?.data_available) return null;
  const { rows: pool, tuple: usedTuple, differs, relaxed } = rangeRows(metric.rows, dims, undefined, {
    supportedKeys: FOB_PICK.supportedKeys,
    tuple,
  });
  if (pool.length === 0) return null;

  let bestHighRow = null;
  let bestHighVal = null;
  let bestLowRow = null;
  let bestLowVal = null;

  for (const r of pool) {
    const hv = toNumber(r.high_value);
    const lv = toNumber(r.low_value);
    if (hv != null && (bestHighVal == null || hv > bestHighVal)) {
      bestHighVal = hv;
      bestHighRow = r;
    }
    if (lv != null && (bestLowVal == null || lv < bestLowVal)) {
      bestLowVal = lv;
      bestLowRow = r;
    }
  }

  if (bestHighRow == null || bestLowRow == null) return null;

  return {
    high_value: bestHighVal,
    low_value: bestLowVal,
    high_date: bestHighRow.high_date,
    low_date: bestLowRow.low_date,
    high_origin: bestHighRow.high_origin,
    low_origin: bestLowRow.low_origin,
    high_variety: realValue(bestHighRow.variety),
    low_variety: realValue(bestLowRow.variety),
    high_date_display: bestHighRow.high_date_display,
    low_date_display: bestLowRow.low_date_display,
    tupleApplied: !!usedTuple,
    usedTuple,
    differs,
    relaxed,
  };
}

// ---------- trend styling ----------

/**
 * Map a signed delta to a lucide trend icon + Tailwind color classes.
 *
 *   n > 0 → TrendingUp   · green  (var(--bloom-green))
 *   n < 0 → TrendingDown · red    (red-500/600)
 *   n === 0 → Minus      · gray   (#828382) — design requirement: zero still
 *     renders a neutral indicator so the card keeps a visual anchor.
 *   non-finite → null — caller omits the badge entirely.
 */
function trendStyle(n0) {
  const n = toNumber(n0);
  if (n == null) return null;
  if (n > 0) {
    return {
      Icon: TrendingUp,
      badgeClass: 'bg-bloom-green/14 dark:bg-bloom-green/18',
      textClass: 'text-bloom-green',
    };
  }
  if (n < 0) {
    return {
      Icon: TrendingDown,
      badgeClass: 'bg-red-500/10 dark:bg-red-500/15',
      textClass: 'text-red-600 dark:text-red-500',
    };
  }
  return {
    Icon: Minus,
    badgeClass: 'bg-[#828382]/12 dark:bg-gray-400/12',
    textClass: 'text-[#828382] dark:text-gray-400',
  };
}

const IconBadge = ({ deltaPct, size = 14 }) => {
  const style = trendStyle(deltaPct);
  if (!style) return null;
  const { Icon, badgeClass, textClass } = style;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md p-1 ${badgeClass}`}
      aria-hidden
    >
      <Icon size={size} strokeWidth={2.25} className={textClass} />
    </span>
  );
};

const TrendLine = ({ deltaPct, suffix }) => {
  const style = trendStyle(deltaPct);
  if (!style) return null;
  return (
    <div className="mt-[4px] flex items-center gap-1 text-[11px]">
      <span className={`font-medium ${style.textClass}`}>{formatSignedPercent(deltaPct)}</span>
      {suffix ? <span className="text-[#282A29] dark:text-white">{suffix}</span> : null}
    </div>
  );
};

// ---------- tile shells ----------

/**
 * Card shell shared by every scorecard tile in this file.
 *
 * Optional click affordance: when `onClick` is provided the entire tile
 * becomes a button-like element (role=button, focus ring, hover) and an
 * `ExternalLink` glyph is rendered in the top-right corner so users see at
 * a glance that the tile navigates somewhere. `clickAriaLabel` describes
 * the destination for screen readers.
 *
 * The left accent bar tracks `deltaPct`: red when the delta is negative,
 * green otherwise (including zero and tiles with no delta at all).
 */
const TileFrame = ({
  label,
  sublabel,
  sublabelTitle,
  pickedTitle,
  deltaPct,
  children,
  onClick,
  clickAriaLabel,
}) => {
  const interactive = typeof onClick === 'function';
  const deltaNum = toNumber(deltaPct);
  const accentClass =
    deltaNum != null && deltaNum < 0 ? 'bg-red-600 dark:bg-red-500' : 'bg-bloom-green';

  const handleKeyDown = (e) => {
    if (!interactive) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(e);
    }
  };

  return (
    <div
      className={[
        'flex-1 min-w-0 md:min-w-[210px] rounded-[8px] border border-[#E2E2E2] dark:border-[#3a3c3b]',
        'bg-white dark:bg-[#191919] overflow-hidden flex',
        interactive
          ? 'cursor-pointer transition-colors hover:border-bloom-green hover:bg-[#F0F5F2] dark:hover:bg-[#1d1d1d] focus:outline-none focus-visible:ring-2 focus-visible:ring-bloom-green focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-[#191919]'
          : '',
      ].join(' ')}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? clickAriaLabel : undefined}
      title={pickedTitle || undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={interactive ? handleKeyDown : undefined}
    >
      <div className={`w-[4px] shrink-0 ${accentClass}`} aria-hidden />
      <div className="min-w-0 flex-1 px-[14px] py-[12px] max-[479px]:px-3 max-[479px]:py-[10px]">
        <div className="flex items-start justify-between gap-2">
          <div
            className="min-w-0 truncate whitespace-nowrap text-[12px] text-[#828382] dark:text-gray-400"
            title={typeof label === 'string' ? label : undefined}
          >
            {label}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {sublabel ? (
              <span
                className="shrink-0 whitespace-nowrap text-right text-[11px] text-[#828382] dark:text-gray-400"
                title={sublabelTitle || undefined}
              >
                {sublabel}
              </span>
            ) : null}
            {trendStyle(deltaPct) ? (
              <IconBadge deltaPct={deltaPct} />
            ) : (
              <span className="inline-block h-[22px] w-[22px] shrink-0" aria-hidden />
            )}
            {interactive ? (
              <ExternalLink
                size={14}
                strokeWidth={2.25}
                className="text-[#828382] dark:text-gray-400"
                aria-hidden
              />
            ) : null}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};

const ValueTile = ({ label, sublabel, sublabelTitle, pickedTitle, value, deltaPct, trendSuffix, footerParts }) => (
  <TileFrame label={label} sublabel={sublabel} sublabelTitle={sublabelTitle} pickedTitle={pickedTitle} deltaPct={deltaPct}>
    <div className="mt-[4px] kpi-hero-value font-semibold text-[#282A29] dark:text-white">{value}</div>
    {deltaPct != null ? <TrendLine deltaPct={deltaPct} suffix={trendSuffix} /> : null}
    {footerParts && footerParts.length ? (
      <CaptionLine
        parts={footerParts}
        lines={2}
        className="mt-[4px] text-[11px] leading-[16.5px] text-[#828382] dark:text-gray-400"
        title={captionText(footerParts)}
      />
    ) : null}
  </TileFrame>
);

/**
 * Caption parts for a pick: the selection tuple values as separate parts (never
 * one string with literal dots), or the plain label (scope / "All selected").
 */
const pickParts = (pick) =>
  pick?.tuple && Object.keys(pick.tuple).length
    ? Object.values(pick.tuple).map((v) => captionPart(v))
    : [captionPart(pick?.label)];

/**
 * Hero number with the picked label (origin / terminal scope) beside it, so the
 * label costs no extra row. The hero never shrinks; the label wraps in full
 * (its tile grows) with the text also in `title`.
 */
const HeroRow = ({ heroClass, hero, pickParts: parts }) => (
  <div className="mt-[4px] flex min-w-0 items-end gap-2">
    <div className={`shrink-0 whitespace-nowrap ${heroClass}`}>{hero}</div>
    {parts && parts.length ? (
      <div className="min-w-0 flex-1 pb-[2px]">
        <CaptionLine parts={parts} lines={2} className="text-[11px] leading-[13px] text-[#828382] dark:text-gray-400" />
      </div>
    ) : null}
  </div>
);

// Hero numbers scale in three steps via `.kpi-hero-value`
// (index.css): 24/18px on phones (< 480px), 38/30px on the portrait signage
// panel (480–1023px), and dev's original 20/16px on desktop (lg+). Only the row
// layout below is adapted for small screens / the portrait panel.
const ScorecardRow = ({ children, ...rest }) => (
  // < 480px: single column. 480-767px (portrait signage panel): 2-up grid with
  // the last card spanning both columns only when the count is odd. md+ (desktop): the original 3-up flex
  // row, unchanged.
  <div className="grid grid-cols-1 min-[480px]:grid-cols-2 gap-2 md:flex md:flex-row md:gap-3 min-[480px]:[&>*:last-child:nth-child(odd)]:col-span-2 md:[&>*:last-child]:col-span-1" {...rest}>
    {children}
  </div>
);

// ---------- scorecard groups ----------

// Placeholder block: transparent "0" in the same typography as the real text, so
// its height equals the loaded line's height at every breakpoint.
const Bone = ({ className }) => (
  <div className={`skeleton-sheen rounded-[6px] text-transparent ${className}`} aria-hidden>0</div>
);

// Loading placeholder for a scorecard row, built from the real TileFrame so the
// frame, padding and header row match the loaded tiles. `tiles` describes each
// loaded tile's normal structure: the hero typography class and one class string
// per caption line (margin, visibility and line height), so every breakpoint
// reserves exactly the loaded height.
const L = 'text-[11px] leading-[16.5px]';
const HERO_LG = 'text-[20px] leading-tight';
const FOB_CAPS = [`mt-[4px] ${L} hidden lg:block`, `mt-[4px] ${L} hidden md:block lg:hidden`, `mt-[2px] ${L} hidden md:block lg:hidden`, `mt-[4px] ${L} md:hidden`];
const SKELETON_TILES = {
  fob: [
    { hero: 'kpi-hero-value', caps: FOB_CAPS },
    { hero: 'kpi-hero-value', caps: FOB_CAPS },
    { range: true },
  ],
  volume: Array(3).fill({ hero: HERO_LG, caps: [`mt-[4px] ${L}`, `mt-[2px] ${L}`] }),
  terminal: [
    { hero: 'kpi-hero-value', caps: [`mt-[4px] ${L}`] },
    { hero: HERO_LG, caps: [`mt-[4px] ${L}`, `mt-[2px] ${L}`] },
    { hero: HERO_LG, caps: ['mt-[4px] text-[11px] leading-[14px]', 'mt-[2px] text-[11px] leading-[14px]', 'mt-[2px] text-[11px] leading-[14px]'] },
  ],
};

export const ScorecardSkeleton = ({ tiles = SKELETON_TILES.fob }) => (
  <ScorecardRow role="status" aria-busy="true" aria-label="Loading scorecards">
    {tiles.map((t, i) => (
      <TileFrame key={i} label={<Bone className="w-28 text-[12px]" />}>
        {t.range ? (
          <div className="mt-[2px] flex gap-4">
            {[0, 1].map((k) => (
              <div key={k} className="kpi-range-half flex-1 min-w-0">
                <Bone className="kpi-range-heading w-3/4" />
                <Bone className="w-full text-[11px] leading-[13px]" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <Bone className={`mt-[4px] w-24 ${t.hero}`} />
            {t.caps.map((c, j) => <Bone key={j} className={`w-40 ${c}`} />)}
          </>
        )}
      </TileFrame>
    ))}
  </ScorecardRow>
);

/**
 * FOB caption block, numbers only (the picked label sits beside the hero).
 * lg+: one joined line "A  ->  B". md..lg: two lines. < md: one compact line
 * with shortened wording. Each entry is { full, compact }. Every line is a
 * wrapped row with the full text in `title`.
 */
const FobCaptions = ({ compare, value }) => {
  const cls = 'min-w-0 break-words [overflow-wrap:anywhere] text-[11px] text-[#828382] dark:text-gray-400';
  const fullJoined = [compare?.full, value?.full].filter(Boolean).join('  →  ');
  const compactJoined = [compare?.compact, value?.compact].filter(Boolean).join(' → ');
  if (!fullJoined) return null;
  return (
    <>
      <div className={`mt-[4px] hidden ${cls} lg:block`} title={fullJoined}>
        {fullJoined}
      </div>
      {compare?.full ? (
        <div className={`mt-[4px] ${cls} hidden md:block lg:hidden`} title={compare.full}>
          {compare.full}
        </div>
      ) : null}
      {value?.full ? (
        <div className={`${compare?.full ? 'mt-[2px]' : 'mt-[4px]'} ${cls} hidden md:block lg:hidden`} title={value.full}>
          {value.full}
        </div>
      ) : null}
      <div className={`mt-[4px] ${cls} md:hidden`} title={compactJoined}>
        {compactJoined}
      </div>
    </>
  );
};

/**
 * Three FOB-price scorecards: avg vs last year, this week vs last week, and a
 * range-spread tile showing the high / low extremes with their origins + dates.
 *
 * @param {object}  props
 * @param {object}  props.apiParams - forwarded to the FOB-price-pulse query hook.
 * @param {object} [props.dims]     - row-filter dimensions (e.g. commodity,
 *   origin). Array-valued dims with length !== 1 are treated as wildcards.
 */
export const FobPricePulseScorecards = ({ apiParams, dims }) => {
  const { data, isLoading } = useFobPricePulseScorecards(apiParams);
  const cards = data?.cards || {};
  const rangeLabel = formatRangeShort(apiParams?.dateStart, apiParams?.dateEnd);
  const rangeTitle = formatRangeFull(apiParams?.dateStart, apiParams?.dateEnd);

  // One selection for the whole row of tiles (picked once, shared by siblings).
  const shared = useMemo(
    () => resolvePicks(cards, [
      { metric: METRICS.FOB_AVG_VS_LY, dims, opts: FOB_PICK },
      { metric: METRICS.FOB_VS_LAST_WEEK, dims, opts: FOB_PICK },
    ]),
    [cards, dims]
  );

  const tile1 = useMemo(() => {
    const pick = shared.picks[0];
    const row = pick.row;
    const kind = headlineKind(row, FOB_DELTA_KEYS, ['value']);
    if (!kind) return null;
    const isPct = kind === 'pct';
    return (
      <TileFrame label="% Δ from last year" sublabel={rangeLabel} sublabelTitle={rangeTitle} pickedTitle={pick.title} deltaPct={isPct ? row?.delta_pct ?? null : null}>
        <HeroRow
          heroClass={`kpi-hero-value font-semibold ${(isPct && trendStyle(row?.delta_pct)?.textClass) || 'text-[#282A29] dark:text-white'}`}
          hero={isPct ? formatSignedPercent(row?.delta_pct) : formatUsd(row?.value)}
          pickParts={pickParts(pick)}
        />
        <FobCaptions
          compare={row?.compare_value != null ? { full: `Last year ${formatUsd(row.compare_value)}`, compact: `Last yr ${formatUsd(row.compare_value)}` } : null}
          value={row?.value != null ? { full: `This period ${formatUsd(row.value)}`, compact: `This yr ${formatUsd(row.value)}` } : null}
        />
      </TileFrame>
    );
  }, [shared, rangeLabel, rangeTitle]);

  const tile2 = useMemo(() => {
    const pick = shared.picks[1];
    const row = pick.row;
    const kind = headlineKind(row, FOB_DELTA_KEYS, ['value']);
    if (!kind) return null;
    const isPct = kind === 'pct';
    const curWeekNum = (() => {
      const m = /W(\d+)/.exec(row?.current_iso_week || '');
      return m ? m[1] : null;
    })();
    const priorWeekNum = (() => {
      const m = /^(\d{4})-W(\d+)$/.exec(row?.current_iso_week || '');
      if (!m) return null;
      const w = Number(m[2]);
      return w > 1 ? String(w - 1).padStart(2, '0') : null;
    })();
    const currentLabel = curWeekNum ? `This week W${curWeekNum}` : 'This week';
    const priorLabel = priorWeekNum ? `Last week W${priorWeekNum}` : 'Last week';
    return (
      <TileFrame label="% Δ from last week" sublabel={isoWeekLabel(row?.current_iso_week) || rangeLabel} sublabelTitle={rangeTitle} pickedTitle={pick.title} deltaPct={isPct ? row?.delta_pct ?? null : null}>
        <HeroRow
          heroClass={`kpi-hero-value font-semibold ${(isPct && trendStyle(row?.delta_pct)?.textClass) || 'text-[#282A29] dark:text-white'}`}
          hero={isPct ? formatSignedPercent(row?.delta_pct) : formatUsd(row?.value)}
          pickParts={pickParts(pick)}
        />
        <FobCaptions
          compare={row?.compare_value != null ? { full: `${priorLabel} ${formatUsd(row.compare_value)}`, compact: `${priorWeekNum ? `W${priorWeekNum}` : 'Last wk'} ${formatUsd(row.compare_value)}` } : null}
          value={row?.value != null ? { full: `${currentLabel} ${formatUsd(row.value)}`, compact: `${curWeekNum ? `W${curWeekNum}` : 'This wk'} ${formatUsd(row.value)}` } : null}
        />
      </TileFrame>
    );
  }, [shared, rangeLabel, rangeTitle]);

  const tile3 = useMemo(() => {
    const row = getFobRangeSpreadAggregated(cards, dims, shared.tuple);
    if (!hasAnyNumber(row, FOB_RANGE_KEYS)) return null;
    const highDateText =
      row?.high_date_display != null && row.high_date_display !== ''
        ? row.high_date_display
        : formatShortDate(row?.high_date);
    const lowDateText =
      row?.low_date_display != null && row.low_date_display !== ''
        ? row.low_date_display
        : formatShortDate(row?.low_date);
    const spreadLabel = row?.tupleApplied
      ? Object.values(row.usedTuple || {}).join(' · ')
      : null;
    // Meta parts: date, the variety of the extreme row, other tuple values,
    // then the origin last (its long port names wrap last). Exact repeats are
    // dropped; nothing is clipped.
    const highParts = rangeMetaParts(highDateText, { origin: row?.high_origin, variety: row?.high_variety }, row?.usedTuple);
    const lowParts = rangeMetaParts(lowDateText, { origin: row?.low_origin, variety: row?.low_variety }, row?.usedTuple);
    const spreadNotes = [row?.relaxed ? RELAXED_NOTE : null, row?.differs ? DIFFERS_NOTE : null].filter(Boolean);
    const spreadTitle = [
      spreadLabel ? `Highest and lowest for ${spreadLabel}` : null,
      spreadNotes.length ? `(${spreadNotes.join('; ')})` : null,
    ].filter(Boolean).join(' ') || undefined;
    return (
      <TileFrame
        label="Range spread"
        sublabel={rangeLabel}
        sublabelTitle={rangeTitle}
        pickedTitle={spreadTitle}
      >
        <div className="mt-[2px] flex gap-4">
          <div className="kpi-range-half flex-1 min-w-0">
            <div className="kpi-range-heading font-semibold text-[#282A29] dark:text-white" title="Highest">
              <span className="kpi-range-word font-normal text-[#282A29] dark:text-white">Highest:</span>
              <span className="kpi-range-arrow font-normal text-bloom-green" aria-hidden>&#8593;</span>{' '}
              <span className="text-bloom-green">{formatUsd(row?.high_value)}</span>
            </div>
            <CaptionLine parts={highParts} lines={2} className="text-[11px] leading-[13px] text-[#828382] dark:text-gray-400" />
          </div>
          <div className="kpi-range-half flex-1 min-w-0">
            <div className="kpi-range-heading font-semibold text-[#282A29] dark:text-white" title="Lowest">
              <span className="kpi-range-word font-normal text-[#282A29] dark:text-white">Lowest:</span>
              <span className="kpi-range-arrow font-normal text-red-600 dark:text-red-500" aria-hidden>&#8595;</span>{' '}
              <span className="text-red-600 dark:text-red-500">{formatUsd(row?.low_value)}</span>
            </div>
            <CaptionLine parts={lowParts} lines={2} className="text-[11px] leading-[13px] text-[#828382] dark:text-gray-400" />
          </div>
        </div>
      </TileFrame>
    );
  }, [cards, dims, shared, rangeLabel, rangeTitle]);

  const tiles = [tile1, tile2, tile3].filter(Boolean);
  if (tiles.length === 0) return isLoading ? <ScorecardSkeleton tiles={SKELETON_TILES.fob} /> : null;
  return (
    <ScorecardRow>
      {tiles.map((tile, idx) => React.cloneElement(tile, { key: `fob-${idx}` }))}
    </ScorecardRow>
  );
};

/**
 * Three volume-by-origin scorecards: volume vs last year, week-over-week change
 * with the two week-sum lines, and volume vs 3-year average.
 *
 * @param {object}  props
 * @param {object}  props.apiParams - forwarded to the volume-by-origin query hook.
 * @param {object} [props.dims]
 */
export const VolumeByOriginScorecards = ({ apiParams, dims }) => {
  const { data, isLoading } = useVolumeByOriginScorecards(apiParams);
  const cards = data?.cards || {};
  const rangeLabel = formatRangeShort(apiParams?.dateStart, apiParams?.dateEnd);
  const rangeTitle = formatRangeFull(apiParams?.dateStart, apiParams?.dateEnd);

  const tile1 = useMemo(() => {
    const row = getScorecardRow(cards, METRICS.VOL_SUM_VS_LY, dims);
    if (!row) return null;
    return (
      <TileFrame label="% Δ from last year" sublabel={rangeLabel} sublabelTitle={rangeTitle} deltaPct={row.delta_pct}>
        <div className={`mt-[4px] text-[20px] font-semibold leading-tight ${trendStyle(row.delta_pct)?.textClass || 'text-[#282A29] dark:text-white'}`}>
          {formatSignedPercent(row.delta_pct)}
        </div>
        {row.compare_value != null ? (
          <div className="mt-[4px] text-[11px] text-[#828382] dark:text-gray-400">
            Last year: {formatLbsCompact(row.compare_value)}
          </div>
        ) : null}
        {row.value != null ? (
          <div className="mt-[2px] text-[11px] text-[#828382] dark:text-gray-400">
            This period: {formatLbsCompact(row.value)}
          </div>
        ) : null}
      </TileFrame>
    );
  }, [cards, dims, rangeLabel, rangeTitle]);

  const tile2 = useMemo(() => {
    const row = getScorecardRow(cards, METRICS.VOL_WOW, dims);
    if (!row) return null;
    const weekNum = (s) => {
      if (!s) return '';
      const m = /W(\d+)/.exec(s);
      return m ? m[1] : s;
    };
    const priorLabel = row.prior_iso_week ? `Week ${weekNum(row.prior_iso_week)}` : 'Last week';
    const currentLabel = row.current_iso_week ? `Week ${weekNum(row.current_iso_week)}` : 'This week';
    // The headline value here is itself the percent, so it drives the badge icon.
    return (
      <TileFrame label="% Δ from last week" sublabel={isoWeekLabel(row.current_iso_week) || rangeLabel} sublabelTitle={rangeTitle} deltaPct={row.value}>
        <div className={`mt-[4px] text-[20px] font-semibold leading-tight ${trendStyle(row.value)?.textClass || 'text-[#282A29] dark:text-white'}`}>
          {formatSignedPercent(row.value)}
        </div>
        {row.prior_sum != null ? (
          <div className="mt-[4px] text-[11px] text-[#828382] dark:text-gray-400">
            {priorLabel}: {formatLbsCompact(row.prior_sum)}
          </div>
        ) : null}
        {row.current_sum != null ? (
          <div className="mt-[2px] text-[11px] text-[#828382] dark:text-gray-400">
            {currentLabel}: {formatLbsCompact(row.current_sum)}
          </div>
        ) : null}
      </TileFrame>
    );
  }, [cards, dims, rangeLabel, rangeTitle]);

  const tile3 = useMemo(() => {
    const row = getScorecardRow(cards, METRICS.VOL_VS_3YR, dims);
    if (!row) return null;
    return (
      <TileFrame label="% Δ vs 3-yr avg" sublabel={rangeLabel} sublabelTitle={rangeTitle} deltaPct={row.delta_pct}>
        <div className={`mt-[4px] text-[20px] font-semibold leading-tight ${trendStyle(row.delta_pct)?.textClass || 'text-[#282A29] dark:text-white'}`}>
          {formatSignedPercent(row.delta_pct)}
        </div>
        {row.compare_value != null ? (
          <div className="mt-[4px] text-[11px] text-[#828382] dark:text-gray-400">
            3-yr avg: {formatLbsCompact(row.compare_value)}
          </div>
        ) : null}
        {row.value != null ? (
          <div className="mt-[2px] text-[11px] text-[#828382] dark:text-gray-400">
            This period: {formatLbsCompact(row.value)}
          </div>
        ) : null}
      </TileFrame>
    );
  }, [cards, dims, rangeLabel, rangeTitle]);

  const tiles = [tile1, tile2, tile3].filter(Boolean);
  if (tiles.length === 0) {
    return isLoading ? <ScorecardSkeleton tiles={SKELETON_TILES.volume} /> : null;
  }
  return <ScorecardRow>{tiles.map((tile, idx) => React.cloneElement(tile, { key: `vol-${idx}` }))}</ScorecardRow>;
};

/**
 * Three terminal-price scorecards: highest terminal market, this week vs last
 * week, and the FOB−Terminal price gap.
 *
 * @param {object}  props
 * @param {object}  props.apiParams  - forwarded to the terminal-price query hook.
 * @param {object} [props.dims]      - dims for the top-market and vs-last-week tiles.
 * @param {object} [props.gapDims]   - dims for the gap tile, which keys on a
 *   different dimension set than the other two. Falls back to `dims` when omitted.
 */
export const TerminalPricePulseScorecards = ({ apiParams, dims, gapDims }) => {
  const { data, isLoading } = useTerminalPricePulseScorecards(apiParams);
  const cards = data?.cards || {};
  const rangeLabel = formatRangeShort(apiParams?.dateStart, apiParams?.dateEnd);
  const rangeTitle = formatRangeFull(apiParams?.dateStart, apiParams?.dateEnd);

  const gapSrc = gapDims || dims;
  // Gap first: it is the tile with a % and a variety; the other tiles follow it.
  const shared = useMemo(
    () => resolvePicks(cards, [
      { metric: METRICS.FOB_TERM_GAP, dims: gapSrc, opts: TERM_GAP_PICK },
      { metric: METRICS.TERM_HIGHEST, dims, opts: TERM_HIGHEST_PICK },
      { metric: METRICS.TERM_VS_LAST_WEEK, dims, opts: TERM_WOW_PICK },
    ]),
    [cards, dims, gapSrc]
  );

  const tile1 = useMemo(() => {
    const pick = shared.picks[1];
    const row = pick.row;
    if (!hasAnyNumber(row, TERM_HIGHEST_KEYS)) return null;
    // Aggregate rows (Terminal chip = wildcard) carry destination=NULL and
    // expose `top_destination` for display — the terminal where the overall
    // max occurred. Per-terminal rows carry `destination` directly.
    const destinationLabel = row.destination || row.top_destination;
    const labelHasDestination =
      pick.label && destinationLabel && pick.label.toLowerCase().includes(String(destinationLabel).toLowerCase());
    const footerParts = [
      ...pickParts(pick),
      labelHasDestination ? null : captionPart(destinationLabel),
    ];
    return (
      <ValueTile
        label="Highest terminal price"
        sublabel={formatDm(row.date) || rangeLabel}
        sublabelTitle={rangeTitle}
        pickedTitle={pick.title}
        value={formatUsd(row.value)}
        footerParts={footerParts}
      />
    );
  }, [shared, rangeLabel, rangeTitle]);

  const tile2 = useMemo(() => {
    const pick = shared.picks[2];
    const row = pick.row;
    const kind = headlineKind(row, TERM_DELTA_KEYS, ['value']);
    if (!kind) return null;
    const isPct = kind === 'pct';
    const weekText = isoWeekLabel(row.current_iso_week ?? row.as_of_date) || rangeLabel;
    return (
      <TileFrame label="% Δ from last week" sublabel={weekText} sublabelTitle={rangeTitle} pickedTitle={pick.title} deltaPct={isPct ? row.delta_pct : null}>
        <HeroRow
          heroClass={`text-[20px] font-semibold leading-tight ${(isPct && trendStyle(row.delta_pct)?.textClass) || 'text-[#282A29] dark:text-white'}`}
          hero={isPct ? formatSignedPercent(row.delta_pct) : formatUsd(row.value)}
          pickParts={pickParts(pick)}
        />
        {[
          row.compare_value != null ? `Last week: ${formatUsd(row.compare_value)}` : null,
          row.value != null ? `This week: ${formatUsd(row.value)}` : null,
        ].filter(Boolean).map((line, i) => (
          <div
            key={i}
            className={`${i === 0 ? 'mt-[4px]' : 'mt-[2px]'} min-w-0 break-words [overflow-wrap:anywhere] text-[11px] text-[#828382] dark:text-gray-400`}
            title={line}
          >
            {line}
          </div>
        ))}
      </TileFrame>
    );
  }, [shared, rangeLabel, rangeTitle]);

  const tile3 = useMemo(() => {
    const pick = shared.picks[0];
    const row = pick.row;
    const kind = headlineKind(row, TERM_GAP_KEYS, ['terminal_max']);
    if (!kind) return null;
    const isPct = kind === 'pct';
    const gapPctStyle = trendStyle(row.gap_pct);
    const hasFob = row.fob_avg != null;
    const hasTerm = row.terminal_max != null;
    // The gap compares the NY terminal's own quality / appearance with all FOB
    // origins, so the caption always comes from the returned fields, never from
    // the selected chips.
    const rowSpecific = (v) => (typeof v === 'string' && v.trim() && !isSentinelValue(v) ? v.trim() : null);
    const classifierParts = [rowSpecific(row.terminal_quality), rowSpecific(row.terminal_appearance)]
      .filter(Boolean).map((v) => captionPart(v));
    return (
      <TileFrame
        label="New York gap from FOB"
        sublabel={isoWeekLabel(row.current_iso_week ?? row.as_of_date) || rangeLabel}
        sublabelTitle={rangeTitle}
        pickedTitle={pick.title}
        deltaPct={isPct ? row.gap_pct : null}
      >
        <HeroRow
          heroClass={`text-[20px] font-semibold leading-tight ${(isPct && gapPctStyle?.textClass) || 'text-[#282A29] dark:text-white'}`}
          hero={isPct ? formatSignedPercent(row.gap_pct) : formatUsd(row.terminal_max)}
          pickParts={pickParts(pick)}
        />
        {(() => {
          const items = [
            hasTerm
              ? {
                  parts: [isPct ? formatUsd(row.terminal_max) : null, 'Highest Terminal', ...classifierParts],
                  cls: 'text-[#828382] dark:text-gray-400',
                }
              : null,
            hasFob && hasTerm && row.gap_absolute != null
              ? {
                  parts: [`${formatSignedUsd(row.gap_absolute)} vs FOB`],
                  cls: `font-medium ${gapPctStyle?.textClass || 'text-[#828382] dark:text-gray-400'}`,
                }
              : null,
            hasFob
              ? { parts: [formatUsd(row.fob_avg), 'Avg FOB'], cls: 'text-[#828382] dark:text-gray-400' }
              : null,
          ].filter(Boolean);
          return items.map((it, i) => (
            <CaptionLine
              key={i}
              parts={it.parts}
              lines={2}
              className={`${i === 0 ? 'mt-[4px]' : 'mt-[2px]'} text-[11px] leading-[14px] ${it.cls}`}
            />
          ));
        })()}
      </TileFrame>
    );
  }, [shared, rangeLabel, rangeTitle]);

  const tiles = [tile1, tile2, tile3].filter(Boolean);
  // First load shows a skeleton; once settled, empty tiles are hidden.
  if (tiles.length === 0) return isLoading ? <ScorecardSkeleton tiles={SKELETON_TILES.terminal} /> : null;

  return <ScorecardRow>{tiles.map((tile, idx) => React.cloneElement(tile, { key: `term-${idx}` }))}</ScorecardRow>;
};

export { TileFrame, ScorecardRow, trendStyle };
